import { useGraphDocument } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocument"
import * as Y from "yjs"
import { useY } from "react-yjs"
import { EditorGraphDto } from "@features/builder/types"
import { useMemo } from "react"

// This hook makes graph document data "reactive".
// const { nodes } = useGraphDocumentData()
// Whenever document "nodes" map changes, it'll make your component rerender, making sure you have access to updated nodes.
//
// Reading values directly from document (as below) won't rerender your component whenever data changes.
// const nodes = Object.values(graphDocument.getMap("nodes").ToJSON())
//
// It also improves typescript support that plain "yjs" has on pretty low level.

// Stable fallback doc used when the real document hasn't arrived yet.
// Yjs types passed to useY must always belong to a Y.Doc — detached types throw on observeDeep.
const fallbackDoc = new Y.Doc()
const fallbackGraph = fallbackDoc.getMap("graph")
fallbackGraph.set("id", new Y.Text())
fallbackGraph.set("data", new Y.Text())
fallbackGraph.set("nodes", new Y.Map())
fallbackGraph.set("groups", new Y.Map())

type GraphMapJson = {
  id?: string
  data?: string
  nodes?: Record<string, string>
  groups?: Record<string, string>
}

export const useGraphDocumentData = (): EditorGraphDto => {
  const graphDocument = useGraphDocument()
  const graph = useMemo(() => graphDocument?.getMap("graph") ?? fallbackGraph, [graphDocument])

  // Observe the "graph" map itself rather than each child type.
  //
  // Graph metadata is written by *replacing* the value: graph.set("data", new Y.Text(...)).
  // An observer bound to the previous Y.Text instance (useY(graph.get("data"))) never fires for
  // that replacement, so a metadata change made by another user produced no rerender here and
  // downstream consumers — notably the node validation worker — never saw it.
  // "nodes"/"groups" kept working only because their Y.Map instance is stable; only its entries
  // are replaced.
  const graphJson = useY(graph) as GraphMapJson

  return useMemo(
    () => ({
      id: graphJson.id ?? "",
      data: graphJson.data ?? "",
      nodes: Object.values(graphJson.nodes ?? {}),
      groups: Object.values(graphJson.groups ?? {}),
    }),
    [graphJson],
  )
}
